import { useEffect, useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useToast } from "../../../hooks/use-toast";
import { useCreateUser, useUpdateUser } from "../hooks/use-user-queries";
import { OperationConfig, User, UserFormData } from "../types";

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: User | null;
  onSuccess?: () => void;
  operations: OperationConfig[];
}

export function UserFormDialog({
  open,
  onOpenChange,
  user,
  onSuccess,
  operations,
}: UserFormDialogProps) {
  const [formData, setFormData] = useState<UserFormData>({
    email: "",
    name: "",
    password: "",
    permissions: [],
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof UserFormData, string>>
  >({});
  const [apiError, setApiError] = useState<string>("");

  const { toast } = useToast();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  useEffect(() => {
    if (!open) {
      setFormData({
        email: "",
        name: "",
        password: "",
        permissions: [],
      });
      setErrors({});
      setApiError("");
      return;
    }

    if (user) {
      setFormData({
        email: user.email,
        name: user.name,
        password: "",
        permissions: user.permissions,
      });
      setErrors({});
      setApiError("");
      return;
    }

    setFormData({
      email: "",
      name: "",
      password: "",
      permissions: [],
    });
    setErrors({});
    setApiError("");
  }, [user, open]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof UserFormData, string>> = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "E-mail inválido";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }

    if (!user && !formData.password) {
      newErrors.password = "Senha é obrigatória para novos usuários";
    } else if (!user && formData.password && formData.password.length < 8) {
      newErrors.password = "Senha deve ter pelo menos 8 caracteres";
    }

    if (formData.permissions.length === 0) {
      newErrors.permissions = "Pelo menos uma permissão deve ser selecionada";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    setApiError("");

    try {
      if (user) {
        await updateUser.mutateAsync({
          id: user.id,
          updates: {
            email: formData.email,
            name: formData.name,
            permissions: formData.permissions,
          },
        });
        toast({
          title: "Sucesso",
          description: "Usuário atualizado com sucesso!",
        });
      } else {
        if (!formData.password) {
          return;
        }

        await createUser.mutateAsync({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          permissions: formData.permissions,
        });
        toast({
          title: "Sucesso",
          description: "Usuário criado com sucesso!",
        });
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = error?.message || "Erro ao salvar o usuário";
      setApiError(errorMessage);
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handlePermissionToggle = (ops: string) => {
    setFormData((prev) => {
      const hasPermission = prev.permissions.includes(ops);

      if (hasPermission) {
        return {
          ...prev,
          permissions: prev.permissions.filter((p) => p !== ops),
        };
      }

      return {
        ...prev,
        permissions: [...prev.permissions, ops],
      };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>{user ? "Editar Usuário" : "Novo Usuário"}</DialogTitle>
          <DialogDescription>
            {user
              ? "Atualize as informações do usuário e suas permissões"
              : "Crie um novo usuário e defina suas permissões"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {apiError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800 border border-red-200">
              {apiError}
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="John Doe"
            />
            {errors.name && (
              <span className="text-sm text-red-500">{errors.name}</span>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="john@example.com"
              disabled={!!user}
            />
            {errors.email && (
              <span className="text-sm text-red-500">{errors.email}</span>
            )}
          </div>

          {!user && (
            <div className="grid gap-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, password: e.target.value }))
                }
                placeholder="Mínimo de 8 caracteres"
              />
              {errors.password && (
                <span className="text-sm text-red-500">{errors.password}</span>
              )}
            </div>
          )}

          <div className="grid gap-3">
            <Label>Permissões</Label>
            <div className="flex flex-wrap gap-2">
              {operations.map((ops) => {
                const isSelected = formData.permissions.includes(ops.value);
                return (
                  <Badge
                    key={ops.value}
                    role="checkbox"
                    aria-checked={isSelected}
                    aria-label={ops.label}
                    tabIndex={0}
                    className="cursor-pointer select-none transition-all hover:opacity-80 text-sm px-3 py-1.5"
                    style={
                      isSelected
                        ? {
                            backgroundColor: ops.color,
                            borderColor: ops.color,
                            color: "#fff",
                          }
                        : {
                            backgroundColor: "transparent",
                            borderColor: ops.color,
                            color: ops.color,
                          }
                    }
                    onClick={() => handlePermissionToggle(ops.value)}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        handlePermissionToggle(ops.value);
                      }
                    }}
                  >
                    {ops.label}
                  </Badge>
                );
              })}
            </div>
            {errors.permissions && (
              <span className="text-sm text-red-500">{errors.permissions}</span>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={createUser.isPending || updateUser.isPending}
          >
            {createUser.isPending || updateUser.isPending
              ? "Salvando..."
              : user
                ? "Salvar Alterações"
                : "Criar Usuário"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
